import { Injectable, computed, inject, signal } from '@angular/core';
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  typeEventArgs,
  ReadyArgs
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';

export interface AuthUser {
  username?: string;
  email?: string;
  fullName?: string;
  roles: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly keycloak = inject(Keycloak);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

  readonly authenticated = signal(false);
  readonly user = signal<AuthUser | null>(null);

  readonly isLoggedIn = computed(() => this.authenticated());
  readonly displayName = computed(() => this.user()?.fullName ?? this.user()?.username ?? '');

  constructor() {
    // React to Keycloak lifecycle events
    queueMicrotask(() => this.subscribeToEvents());
  }

  private subscribeToEvents(): void {
    // The signal updates on every Keycloak event.
    // We re-read it via a microtask loop driven by Angular's signal scheduler
    // through an effect set up in the component tree if needed.
    this.refreshState();
  }

  refreshState(): void {
    const event = this.keycloakSignal();
    if (event.type === KeycloakEventType.Ready) {
      const authenticated = typeEventArgs<ReadyArgs>(event.args);
      this.authenticated.set(authenticated);
    }

    if (this.keycloak.authenticated) {
      this.authenticated.set(true);
      const profile = this.keycloak.tokenParsed as
        | (Record<string, unknown> & {
            preferred_username?: string;
            email?: string;
            name?: string;
            realm_access?: { roles: string[] };
          })
        | undefined;

      this.user.set({
        username: profile?.preferred_username,
        email: profile?.email,
        fullName: profile?.name,
        roles: profile?.realm_access?.roles ?? []
      });
    } else {
      this.authenticated.set(false);
      this.user.set(null);
    }
  }

  login(redirectUri: string = window.location.origin): Promise<void> {
    return this.keycloak.login({ redirectUri });
  }

  logout(redirectUri: string = window.location.origin): Promise<void> {
    return this.keycloak.logout({ redirectUri });
  }

  account(): void {
    this.keycloak.accountManagement();
  }

  hasRole(role: string): boolean {
    return this.keycloak.hasRealmRole(role);
  }
}
